import React, { useState, useCallback, memo } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Theme,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material';

import { useFormState } from '../../../hooks/useFormState';
import AppDialog from '../../components/varietyPlanner/AppDialog';
import VpRecommendedProducts from './VpRecommendedProducts';
import VpForm from './VpForm';
import { useI18n } from '../../../hooks/useI18n';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import { useQuestionsWithTranslations } from '../../../hooks/varietyPlanner/useQuestionsWithTranslations';

const makeStyles = (theme: Theme) => ({
  dialogTitleContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'baseline',
  },
  dialogContentContainer: {
    marginRight: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  sectionTableContainer: {
    flex: '1 1 auto',
    overflow: 'auto',
    minHeight: 300,
    padding: theme.spacing(2, 1.75, 1.75, 1.75),
  },
  sectionDivider: {
    borderBottom: `1px solid ${theme.palette.tableDivider}`,
  },
});

interface VpDialogProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
}

const VpDialog: React.FC<Readonly<VpDialogProps>> = ({
  handleCloseModal,
  isModalOpen,
}) => {
  const [isTableSectionInitialView, setIsTableSectionInitialView] =
    useState(true);

  const theme = useTheme();
  const styles = makeStyles(theme);

  const { t } = useI18n();

  const { formState, updateFormValue, setNewFormState, resetForm } =
    useFormState();

  const { questions, isLoading, error } = useQuestionsWithTranslations(
    isModalOpen,
    setNewFormState
  );

  const handleFormChange = useCallback(
    (questionId: string, value: string | string[]) => {
      updateFormValue(questionId, value);
    },
    [updateFormValue]
  );

  const onCloseModal = useCallback(() => {
    setIsTableSectionInitialView(true);
    handleCloseModal();
  }, [handleCloseModal]);

  return (
    <AppDialog
      isOpen={isModalOpen}
      handleClose={onCloseModal}
      title={
        <Box sx={styles.dialogTitleContainer}>
          <Typography
            component="p"
            variant="appH2"
            color="questionsForm.formLabel"
          >
            {t(TranslationKeys.variety_planner_modal_title)}
          </Typography>
          <Typography
            component="p"
            variant="appBodyLarge"
            color="questionsForm.formGrey"
            sx={{ marginX: 1 }}
          >
            {t(TranslationKeys.variety_planner_modal_description)}
          </Typography>
        </Box>
      }
      displayCloseButton
    >
      <Box sx={styles.dialogContentContainer}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            <Box sx={{ flex: '0 0 auto' }}>
              <Collapse in={isTableSectionInitialView}>
                <VpForm
                  formState={formState}
                  questions={questions}
                  onFormChange={handleFormChange}
                  onResetForm={resetForm}
                />
              </Collapse>
              {isTableSectionInitialView && (
                <Divider sx={styles.sectionDivider} />
              )}
            </Box>
            <Box sx={styles.sectionTableContainer}>
              <VpRecommendedProducts
                isTableSectionInitialView={isTableSectionInitialView}
                formState={formState}
                onFormChange={handleFormChange}
                handleChangeExpanded={setIsTableSectionInitialView}
                questions={questions}
              />
            </Box>
          </>
        )}
      </Box>
    </AppDialog>
  );
};

export default memo(VpDialog);
